import { useEffect, useState } from "react";

import dayjs from "dayjs";
import "dayjs/locale/ko";
import { AiOutlineClose } from "react-icons/ai";
import { useShallow } from "zustand/react/shallow";

import { createVehicleReservation, fetchVehicles } from "@/apis/reservation";
import ModalLayout from "@/components/Layout/ModalLayout";
import AlertModal from "@/components/Modal/AlertModal";
import useModal from "@/hooks/useModal";
import useCalendarStore from "@/store/calendarStore";
import useLoginStore from "@/store/loginStore";
import useUserStore from "@/store/userStore";
import type { Vehicle } from "@/types/vehicleReservation";
import { cn } from "@/utils/cn";

dayjs.locale("ko");

interface VehicleQuickReservationModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const VehicleQuickReservationModal = ({
  onClose,
  onSuccess,
}: VehicleQuickReservationModalProps) => {
  const [date] = useCalendarStore(useShallow(state => [state.date]));
  const { tokenId } = useLoginStore();
  const user = useUserStore();

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(
    null,
  );
  const [purpose, setPurpose] = useState("");
  const [destination, setDestination] = useState("");
  const [driverName, setDriverName] = useState(user.userName || "");
  const [driverPhone, setDriverPhone] = useState(user.telNo || "");
  const [submitting, setSubmitting] = useState(false);

  const alertModal = useModal();
  const [alertMsg, setAlertMsg] = useState("");

  useEffect(() => {
    fetchVehicles().then(list => {
      setVehicles(list);
      if (list.length > 0) setSelectedVehicleId(prev => prev ?? list[0].vhclId);
    });
  }, []);

  const isPastDate = date.isBefore(dayjs(), "day");

  const handleSubmit = async () => {
    if (selectedVehicleId === null) {
      setAlertMsg("차량을 선택해주세요.");
      alertModal.onOpen();
      return;
    }
    if (!purpose.trim()) {
      setAlertMsg("운행 목적을 입력해주세요.");
      alertModal.onOpen();
      return;
    }
    if (!destination.trim()) {
      setAlertMsg("목적지를 입력해주세요.");
      alertModal.onOpen();
      return;
    }
    if (!driverName.trim()) {
      setAlertMsg("운전자 이름을 입력해주세요.");
      alertModal.onOpen();
      return;
    }
    if (!driverPhone.trim()) {
      setAlertMsg("운전자 연락처를 입력해주세요.");
      alertModal.onOpen();
      return;
    }

    setSubmitting(true);
    try {
      await createVehicleReservation({
        vhclId: selectedVehicleId,
        mbrId: tokenId,
        applicantName: user.userName,
        applicantPhone: user.telNo || "",
        communityName: user.cmtNm || "",
        rsvtDt: date.format("YYYYMMDD"),
        drivingTime: "전일",
        purpose,
        destination,
        driverName,
        driverPhone,
      });
      onSuccess();
    } catch (error: any) {
      setAlertMsg(
        error?.response?.data?.message ||
          "예약에 실패했습니다. 다시 시도해주세요.",
      );
      alertModal.onOpen();
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20";

  return (
    <ModalLayout variant="primary" onClose={onClose}>
      <div className="flex w-[340px] flex-col sm:w-[480px]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h3 className="text-base font-bold text-gray-900">차량 예약 신청</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <AiOutlineClose size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 px-6 py-5">
          {/* Date (read-only) */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">
              예약 날짜
            </label>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              {date.format("YYYY년 M월 D일 (dd)")}
            </div>
          </div>

          {/* Vehicle selector */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">
              차량 선택 <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              {vehicles.map(v => (
                <button
                  key={v.vhclId}
                  type="button"
                  onClick={() => setSelectedVehicleId(v.vhclId)}
                  className={cn(
                    "flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors",
                    v.vhclId === selectedVehicleId
                      ? "border-primary bg-primary text-white"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50",
                  )}
                >
                  {v.vehicleName}
                </button>
              ))}
            </div>
          </div>

          {/* Driving time (fixed) */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">
              운행시간
            </label>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              전일 (00:00 ~ 24:00)
            </div>
          </div>

          {/* Applicant info (read-only) */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500">
                신청자
              </label>
              <div className="min-h-[38px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {user.userName}
              </div>
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500">
                연락처
              </label>
              <div className="min-h-[38px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {user.telNo || "\u00A0"}
              </div>
            </div>
          </div>

          {/* Purpose */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">
              운행 목적 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={purpose}
              onChange={e => setPurpose(e.target.value)}
              placeholder="차량 사용 목적을 입력하세요"
              className={inputClass}
            />
          </div>

          {/* Destination */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">
              목적지 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={destination}
              onChange={e => setDestination(e.target.value)}
              placeholder="이동 목적지를 입력하세요"
              className={inputClass}
            />
          </div>

          {/* Driver */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500">
                운전자 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={driverName}
                onChange={e => setDriverName(e.target.value)}
                placeholder="실제 운전자 이름"
                className={inputClass}
              />
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500">
                운전자 연락처 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={driverPhone}
                onChange={e => setDriverPhone(e.target.value)}
                placeholder="010-0000-0000"
                className={inputClass}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || isPastDate}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "신청 중..." : "예약 신청"}
          </button>
        </div>
      </div>

      {alertModal.isOpen && (
        <AlertModal onClose={alertModal.onClose}>{alertMsg}</AlertModal>
      )}
    </ModalLayout>
  );
};

export default VehicleQuickReservationModal;
